import React from 'react';
import { Search, Shield, Lock, Zap } from 'lucide-react';
import { ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';

interface HeroProps {
  theme: ThemeMode;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onClearSearch: () => void;
}

export const Hero: React.FC<HeroProps> = ({
  theme,
  searchQuery,
  onSearchChange,
  onClearSearch
}) => {
  const isDark = theme === 'dark';
  const { t } = useI18n();

  return (
    <section className="relative pt-4 sm:pt-12 pb-4 sm:pb-4 px-3 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center z-10">
      
      {/* Top Tag Pill */}
      <div className="inline-flex items-center justify-center">
        <div className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-bold border flex items-center gap-1.5 sm:gap-2 ${
          isDark
            ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400'
            : 'bg-emerald-50 border-emerald-300 text-emerald-800 shadow-xs'
        }`}>
          <span>{t('hundredPercentFree', '100% Free')}</span>
          <span className="opacity-40">•</span>
          <span>{t('noSignUp', 'No Sign Up')}</span>
          <span className="opacity-40">•</span>
          <span>{t('worksOffline', 'Works Offline')}</span>
        </div>
      </div>
      
      {/* Main Title / Headline */}
      <h1 className={`mt-3 sm:mt-6 text-2xl sm:text-4xl md:text-6xl font-black tracking-tight leading-tight flex flex-col sm:block ${
        isDark ? 'text-white' : 'text-slate-900'
      }`}>
        <span className="inline-block">450+ </span>
        <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent drop-shadow-xs sm:ml-2">
          {t('heroTitleLine2', 'Free Online Tools')}
        </span>
      </h1>

      {/* Subtitle */}
      <p className={`mt-2.5 sm:mt-4 text-xs sm:text-lg md:text-xl max-w-2xl mx-auto font-medium px-2 leading-relaxed ${
        isDark ? 'text-slate-400' : 'text-slate-600'
      }`}>
        {t('heroDescription', 'All the tools you need in one place. Fast, free, secure and easy to use.')}
      </p>

      {/* Search Bar */}
      <div className="mt-4 sm:mt-8 max-w-2xl mx-auto relative group px-1 sm:px-0">
        <div className={`relative flex items-center rounded-2xl border-2 transition-all duration-300 ${
          isDark
            ? 'bg-[#121522]/90 border-slate-700/80 focus-within:border-purple-500 focus-within:ring-4 focus-within:ring-purple-500/20 shadow-xl shadow-purple-950/20'
            : 'bg-white border-slate-300/90 focus-within:border-purple-600 focus-within:ring-4 focus-within:ring-purple-500/15 shadow-xl shadow-slate-200/80 hover:border-slate-400'
        }`}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('searchPlaceholder', 'Search tools...')}
            className={`w-full py-3 sm:py-4 pl-4 sm:pl-6 pr-10 sm:pr-14 bg-transparent outline-none text-xs sm:text-lg rounded-2xl font-medium ${
              isDark ? 'text-white placeholder-slate-500' : 'text-slate-900 placeholder-slate-400'
            }`}
          />
          <div className="absolute right-3 sm:right-4 p-1 sm:p-2 pointer-events-none">
            <Search className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-slate-400' : 'text-purple-600'}`} />
          </div>
        </div>

        {searchQuery && (
          <button
            onClick={onClearSearch}
            className={`absolute right-10 sm:right-14 top-1/2 -translate-y-1/2 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md ${
              isDark ? 'bg-slate-800 text-slate-300 hover:text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            Clear
          </button>
        )}
      </div>

      {/* Feature Badges below Search Bar */}
      <div className="mt-4 sm:mt-8 grid grid-cols-3 gap-1.5 sm:gap-4 max-w-3xl mx-auto text-left px-0.5 sm:px-0">
        
        {/* Badge 1 */}
        <div className={`p-2 sm:p-4 rounded-xl border flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left gap-1.5 sm:gap-3 transition-all duration-200 ${
          isDark
            ? 'bg-[#0f121d]/80 border-slate-800/80 text-slate-200'
            : 'bg-white border-slate-200/90 text-slate-800 shadow-xs hover:shadow-md hover:border-purple-300'
        }`}>
          <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/30 flex items-center justify-center shrink-0">
            <Shield className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
          </div>
          <div>
            <div className={`text-[10.5px] sm:text-sm font-bold leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              {t('badgeNoRegistration', 'No Registration')}
            </div>
            <div className={`text-[8.5px] sm:text-xs font-medium mt-0.5 sm:mt-0 opacity-80 sm:opacity-100 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('badgeFreeForever', '100% Free Forever')}
            </div>
          </div>
        </div>

        {/* Badge 2 */}
        <div className={`p-2 sm:p-4 rounded-xl border flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left gap-1.5 sm:gap-3 transition-all duration-200 ${
          isDark
            ? 'bg-[#0f121d]/80 border-slate-800/80 text-slate-200'
            : 'bg-white border-slate-200/90 text-slate-800 shadow-xs hover:shadow-md hover:border-purple-300'
        }`}>
          <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/30 flex items-center justify-center shrink-0">
            <Lock className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
          </div>
          <div>
            <div className={`text-[10.5px] sm:text-sm font-bold leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              {t('badgePrivacyFocused', 'Privacy Focused')}
            </div>
            <div className={`text-[8.5px] sm:text-xs font-medium mt-0.5 sm:mt-0 opacity-80 sm:opacity-100 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('badgeYourDataStays', 'Your data stays with you')}
            </div>
          </div>
        </div>

        {/* Badge 3 */}
        <div className={`p-2 sm:p-4 rounded-xl border flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left gap-1.5 sm:gap-3 transition-all duration-200 ${
          isDark
            ? 'bg-[#0f121d]/80 border-slate-800/80 text-slate-200'
            : 'bg-white border-slate-200/90 text-slate-800 shadow-xs hover:shadow-md hover:border-purple-300'
        }`}>
          <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/30 flex items-center justify-center shrink-0">
            <Zap className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
          </div>
          <div>
            <div className={`text-[10.5px] sm:text-sm font-bold leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              {t('badgeSuperFast', 'Super Fast')}
            </div>
            <div className={`text-[8.5px] sm:text-xs font-medium mt-0.5 sm:mt-0 opacity-80 sm:opacity-100 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('badgeInstantResults', 'Instant results')}
            </div>
          </div>
        </div>

      </div>

    </section>
  );
};
